import React, { useState, useEffect } from 'react';
import styles from './TicketDetailsModal.module.scss';
import { userApi } from '../../../../../Utils/API';
import useLogout from '../../../../../hooks/useLogout';
import loadingGif from '../../../../../Assets/Animations/loading.gif';
import { stripTags } from '../../../../../Utils/sanitize';

const TicketDetailsModal = ({ isOpen, onClose, ticket }) => {
    const [details, setDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const logout = useLogout();

    useEffect(() => {
        const fetchTicketDetails = async () => {
            if (!ticket?.id) return;

            setIsLoading(true);
            try {
                const token_type = localStorage.getItem('token_type');
                const access_token = localStorage.getItem('access_token');
                // if (!token_type || !access_token) {
                //     throw new Error('Authorization token not found.');
                // }

                const response = await userApi.get(`/api/support-tickets/${ticket.id}`, {
                    headers: {
                        Authorization: `${token_type} ${access_token}`,
                    },
                });
                setDetails(response.data);
            } catch (err) {
                if (err.response?.status === 401) {
                    logout();
                } else {
                    console.error("Failed to fetch ticket details.", err);
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchTicketDetails();
        } else {
            setDetails(null);
        }
    }, [isOpen, ticket?.id, logout]);
    
    if (!isOpen || !ticket) {
        return null;
    }

    const getStatusClass = (status) => {
        switch (status) {
            case 'Resolved': return styles.statusResolved;
            case 'Closed': return styles.statusClosed;
            case 'In Progress': return styles.statusInProgress;
            case 'Pending Customer Response': return styles.statusPending;
            default: return styles.statusOpen;
        }
    };

    const getInitials = (name) => {
        if (!name) return '';
        const parts = name.split('_');
        if (parts.length > 1) {
            return parts.map(part => part[0]).join('').toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    const displayData = details || ticket;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h3>Ticket Details</h3>
                    <button onClick={onClose} className={styles.closeButton}>
                        <i className="material-icons">close</i>
                    </button>
                </div>
                <div className={styles.modalBody}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <img src={loadingGif} alt="Loading..." />
                        </div>
                    ) : (
                        <>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">confirmation_number</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Ticket No</span>
                                        <span className={styles.detailValue}>{displayData.ticketNo}</span>
                                    </div>
                                </div>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">label</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Title</span>
                                        <span className={styles.detailValue}>{stripTags(displayData.title)}</span>
                                    </div>
                                </div>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">category</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Category</span>
                                        <span className={styles.detailValue}>{displayData.typeStr || displayData.category}</span>
                                    </div>
                                </div>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">task_alt</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Status</span>
                                        <span className={styles.detailValue}><span className={`${styles.status} ${getStatusClass(displayData.statusStr || displayData.status)}`}>{displayData.statusStr || displayData.status}</span></span>
                                    </div>
                                </div>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">person</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Created By</span>
                                        <span className={styles.detailValue}>{displayData.createdBy}</span>
                                    </div>
                                </div>
                                <div className={styles.detailItem}>
                                    <i className="material-icons">calendar_today</i>
                                    <div className={styles.detailText}>
                                        <span className={styles.detailLabel}>Created On</span>
                                        <span className={styles.detailValue}>{formatDate(displayData.createdOn)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.descriptionSection}>
                                <h4>Description</h4>
                                <p>{stripTags(displayData.description) || 'No description provided.'}</p>
                            </div>
                            <div className={styles.historySection}>
                                <h4>History</h4>
                                <div className={styles.timeline}>
                                    {displayData.history && displayData.history.length > 0 ? (
                                        displayData.history.map((item, index) => (
                                            <div className={styles.timelineItem} key={index}>
                                                <div className={styles.timelineAvatar}>{getInitials(item.updatedBy)}</div>
                                                <div className={styles.timelineContent}>
                                                    <span className={styles.historyUser}>{item.updatedBy}</span>
                                                    <span className={styles.historyAction}>{stripTags(item.description)}</span>
                                                    <span className={styles.historyDate}>{formatDate(item.updatedOn)}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No history found for this ticket.</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className={styles.modalFooter}>
                    <button onClick={onClose} className={styles.closeBtn}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default TicketDetailsModal;